/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Button, InputRightElement, useMultiStyleConfig, VisuallyHidden} from '@chakra-ui/react'
import useZipCodeFields from './useZipCodeFields'
import Field from '../../components/field/index'
import {CloseIcon} from '../icons'

const ZipCodeFields = ({form, prefix = '', setShowInput, ...props}) => {
    const fields = useZipCodeFields({form, prefix})
    const style = useMultiStyleConfig('OrderSummary', {})
    const {zipCode} = style
    const code = form.watch('code')
    const {formatMessage} = useIntl()
    const ariaZipLabel = formatMessage({
        defaultMessage: 'Zip Code',
        id: 'use_zip_code_zipcode.label.zip_code',
    })

    return (
        <Box {...style.zipCode?.zipInputContainer} aria-labelledby="code-feedback" {...props}>
            <VisuallyHidden as="label" htmlFor="code">
                {ariaZipLabel}
            </VisuallyHidden>

            <Box {...zipCode?.zipCodeInputFieldContainer}>
                <Field
                    inputProps={{
                        'aria-label': ariaZipLabel,
                        paddingInlineEnd: '5.5rem',
                    }}
                    {...fields.code}
                    label={ariaZipLabel}
                    size="base"
                    shouldDisplaySuccess={false}
                >
                    {code?.length >= 3 ? (
                        <InputRightElement {...zipCode?.zipCodeApplyInputRightElement}>
                            <Button
                                type="submit"
                                variant="primary-not-rounded"
                                size="small"
                                isLoading={form.formState.isSubmitting}
                                // disabled={code?.length < 3}
                                {...zipCode?.zipCodeApplyButton}
                            >
                                <FormattedMessage
                                    defaultMessage="Apply"
                                    id="zip_code_fields.button.apply"
                                />
                            </Button>
                        </InputRightElement>
                    ) : (
                        <></>
                    )}
                </Field>
            </Box>

            <Button
                variant="icon-button-outline"
                size="base"
                onClick={() => setShowInput(false)}
                {...zipCode?.zipCodeInputClearButton}
            >
                <CloseIcon {...zipCode?.zipCodeInputClearIcon} />
            </Button>
        </Box>
    )
}

ZipCodeFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    setShowInput: PropTypes.func,
}

export default ZipCodeFields
